import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Video, MoreVertical, Paperclip, Mic, Send, Image as ImageIcon, FileText, Pill } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';
export function ChatScreen() {
  const navigate = useNavigate();
  const {
    bookingData
  } = useAppContext();
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const doctor = bookingData?.professional || {
    name: 'Dr. Chinedu Okafor',
    specialty: 'General Doctor',
    image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&q=80&w=200&h=200',
    verified: true
  };
  const [messages, setMessages] = useState([{
    id: 1,
    text: 'Hello Oluwaseun, I have received your booking for today at 02:30 PM.',
    sender: 'doctor',
    time: '10:00 AM'
  }, {
    id: 2,
    text: 'Could you please share more details about the symptoms you are experiencing?',
    sender: 'doctor',
    time: '10:01 AM'
  }, {
    id: 3,
    text: 'Hi Doctor. Yes, I have been having a mild fever and headache since yesterday evening.',
    sender: 'user',
    time: '10:05 AM'
  }]);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const handleSend = () => {
    if (message.trim()) {
      setMessages([...messages, {
        id: Date.now(),
        text: message,
        sender: 'user',
        time: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })
      }]);
      setMessage('');
      // Simulate doctor reply if it's the first user message
      if (messages.length === 3) {
        setTimeout(() => {
          setMessages(prev => [...prev, {
            id: Date.now() + 1,
            text: 'Noted. Please make sure you are in a quiet place with good internet for our video call at 2:30 PM.',
            sender: 'doctor',
            time: new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })
          }]);
        }, 2000);
      }
    }
  };
  return <div className="min-h-screen bg-[#E5E7EB] flex flex-col relative" data-id="element-253">
      {/* Header */}
      <div className="bg-white px-4 py-3 flex items-center justify-between shadow-sm z-10 sticky top-0" data-id="element-254">
        <div className="flex items-center" data-id="element-255">
          <button onClick={() => navigate('/home')} className="mr-2 p-2 -ml-2 rounded-full hover:bg-ui-card" data-id="element-256">
            <ArrowLeft size={24} className="text-text-main" data-id="element-257" />
          </button>
          <img src={doctor.image} alt={doctor.name} className="w-10 h-10 rounded-full object-cover mr-3" data-id="element-258" />
          <div data-id="element-259">
            <h2 className="font-bold text-text-main text-sm flex items-center" data-id="element-260">
              {doctor.name}
            </h2>
            <p className="text-xs text-brand-primary font-medium flex items-center" data-id="element-261">
              <span className="w-2 h-2 rounded-full bg-status-success mr-1.5" data-id="element-262"></span>{' '}
              Online
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-4 text-brand-primary" data-id="element-263">
          <button className="p-1" data-id="element-264">
            <Phone size={20} data-id="element-265" />
          </button>
          <button onClick={() => navigate('/video-call')} className="p-1" data-id="element-266">
            <Video size={22} data-id="element-267" />
          </button>
          <button className="p-1 text-text-secondary" data-id="element-268">
            <MoreVertical size={20} data-id="element-269" />
          </button>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-20" data-id="element-270">
        <div className="text-center mb-6" data-id="element-271">
          <span className="bg-white/60 text-text-secondary text-xs px-3 py-1 rounded-full backdrop-blur-sm" data-id="element-272">
            Today
          </span>
        </div>

        {/* Security Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center mx-4 mb-6" data-id="element-273">
          <p className="text-xs text-yellow-800" data-id="element-274">
            Messages are end-to-end encrypted. No one outside of this chat, not
            even Whiz, can read or listen to them.
          </p>
        </div>

        {messages.map(msg => <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-275">
            <div className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm relative ${msg.sender === 'user' ? 'bg-brand-primary text-white rounded-tr-sm' : 'bg-white text-text-main rounded-tl-sm'}`} data-id="element-276">
              <p className="text-sm leading-relaxed" data-id="element-277">{msg.text}</p>
              <span className={`text-[10px] mt-1 block text-right ${msg.sender === 'user' ? 'text-brand-light/80' : 'text-text-secondary'}`} data-id="element-278">
                {msg.time}
              </span>
            </div>
          </div>)}

        {/* Example Prescription Card (Simulated from doctor) */}
        {messages.length > 4 && <div className="flex justify-start" data-id="element-279">
            <div className="max-w-[80%] bg-white rounded-2xl rounded-tl-sm shadow-sm overflow-hidden border border-ui-border" data-id="element-280">
              <div className="bg-brand-light p-3 flex items-center border-b border-ui-border" data-id="element-281">
                <Pill size={18} className="text-brand-primary mr-2" data-id="element-282" />
                <span className="font-bold text-brand-primary text-sm" data-id="element-283">
                  Prescription Issued
                </span>
              </div>
              <div className="p-3" data-id="element-284">
                <p className="text-sm font-medium mb-1" data-id="element-285">Paracetamol 500mg</p>
                <p className="text-xs text-text-secondary mb-3" data-id="element-286">
                  Take 2 tablets every 8 hours for 3 days.
                </p>
                <button onClick={() => navigate('/visit-summary')} className="w-full py-2 bg-brand-primary text-white text-xs font-semibold rounded-lg" data-id="element-287">
                  View Full Summary
                </button>
              </div>
            </div>
          </div>}

        <div ref={messagesEndRef} data-id="element-288" />
      </div>

      {/* Input Area */}
      <div className="fixed bottom-0 left-0 right-0 w-full max-w-[430px] mx-auto bg-white p-3 flex items-end space-x-2 border-t border-ui-border pb-safe" data-id="element-289">
        <button className="p-2.5 text-text-secondary hover:bg-ui-card rounded-full transition-colors shrink-0" data-id="element-290">
          <Paperclip size={22} data-id="element-291" />
        </button>

        <div className="flex-1 bg-ui-card rounded-2xl border border-ui-border flex items-center px-3 py-1 min-h-[44px]" data-id="element-292">
          <input type="text" value={message} onChange={e => setMessage(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSend()} placeholder="Type a message..." className="flex-1 bg-transparent outline-none text-sm py-2" data-id="element-293" />
          <button className="p-1.5 text-text-secondary shrink-0" data-id="element-294">
            <ImageIcon size={20} data-id="element-295" />
          </button>
        </div>

        {message.trim() ? <button onClick={handleSend} className="w-11 h-11 bg-brand-primary text-white rounded-full flex items-center justify-center shrink-0 shadow-md active:scale-95 transition-transform" data-id="element-296">
            <Send size={18} className="ml-1" data-id="element-297" />
          </button> : <button className="w-11 h-11 bg-brand-primary text-white rounded-full flex items-center justify-center shrink-0 shadow-md active:scale-95 transition-transform" data-id="element-298">
            <Mic size={20} data-id="element-299" />
          </button>}
      </div>
    </div>;
}